import { useNavigationIntentStore } from "&/navigationIntent";
import {
  buildDeepLinkTarget,
  failedDeepLinkResult,
  normalizeDeepLinkPayload,
  successDeepLinkResult,
} from "%/deepLinkBridge";
import { getAccessToken } from "%/token";
import type {
  NativeDeepLinkPayload,
  NativeDeepLinkResult,
} from "%/webviewBridge";
import { useLocation, useRouter } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";

const getPathnameFromHref = (href: string) => {
  const pathname = href.split("?")[0].split("#")[0];
  return pathname.endsWith("/") && pathname.length > 1
    ? pathname.slice(0, -1)
    : pathname;
};

export const usePushDeepLinkRouter = () => {
  const { history } = useRouter();
  const location = useLocation();
  const queryClient = useQueryClient();
  const pendingNavigationIntent = useNavigationIntentStore(
    (state) => state.pendingIntent,
  );
  const navigatedNavigationIntent = useNavigationIntentStore(
    (state) => state.navigatedIntent,
  );
  const authReadyRevision = useNavigationIntentStore(
    (state) => state.authReadyRevision,
  );
  const enqueueDeepLinkIntent = useNavigationIntentStore(
    (state) => state.enqueueDeepLinkIntent,
  );
  const markIntentNavigated = useNavigationIntentStore(
    (state) => state.markIntentNavigated,
  );
  const markIntentArrived = useNavigationIntentStore(
    (state) => state.markIntentArrived,
  );
  const clearNavigatedIntent = useNavigationIntentStore(
    (state) => state.clearNavigatedIntent,
  );
  const processedDeepLinkIdsRef = useRef<Set<string>>(new Set());
  const currentPathname = getPathnameFromHref(location.href);

  const handleDeepLink = useCallback(
    (
      payloadOrPath: NativeDeepLinkPayload | string,
      query?: string,
      type?: string,
    ): NativeDeepLinkResult => {
      const payload = normalizeDeepLinkPayload(payloadOrPath, query, type);

      // 크레딧 결제 복귀 — 외부 브라우저 결제분이 앱 캐시에 반영되도록 무효화
      //  (잔액/사용내역은 react-query, 충전내역은 탭 진입 시 재조회)
      if (payload.type === "CREDIT_PAYMENT_RETURN") {
        void queryClient.invalidateQueries({ queryKey: ["business-credit"] });
      }

      try {
        const target = buildDeepLinkTarget(payload);

        if (
          payload.deepLinkId &&
          processedDeepLinkIdsRef.current.has(payload.deepLinkId)
        ) {
          return successDeepLinkResult(payload, target, true);
        }

        enqueueDeepLinkIntent(payload, target);

        return successDeepLinkResult(payload, target);
      } catch (error) {
        console.error("푸시 딥링크 처리 실패:", payload, error);
        return failedDeepLinkResult(payload, error);
      }
    },
    [enqueueDeepLinkIntent, queryClient],
  );

  useEffect(() => {
    if (!pendingNavigationIntent) {
      return;
    }

    if (!getAccessToken()) {
      return;
    }

    history.push(pendingNavigationIntent.target);

    if (pendingNavigationIntent.payload.deepLinkId) {
      processedDeepLinkIdsRef.current.add(
        pendingNavigationIntent.payload.deepLinkId,
      );
    }

    markIntentNavigated(pendingNavigationIntent.id);
  }, [
    pendingNavigationIntent,
    authReadyRevision,
    history,
    markIntentNavigated,
  ]);

  useEffect(() => {
    if (!navigatedNavigationIntent) {
      return;
    }

    const targetPathname = getPathnameFromHref(navigatedNavigationIntent.target);

    if (currentPathname === targetPathname) {
      markIntentArrived(navigatedNavigationIntent.id);
      return;
    }

    if (navigatedNavigationIntent.arrived) {
      clearNavigatedIntent(navigatedNavigationIntent.id);
    }
  }, [
    clearNavigatedIntent,
    currentPathname,
    markIntentArrived,
    navigatedNavigationIntent,
  ]);

  useEffect(() => {
    window.receiveDeepLink = handleDeepLink;
    window.appIsReady = true;

    return () => {
      if (window.receiveDeepLink === handleDeepLink) {
        delete window.receiveDeepLink;
      }
      window.appIsReady = false;
    };
  }, [handleDeepLink]);
};
